/**
 * Label Service Headless Pool
 * Using the unified headless pool system from @things-factory/shell
 */

import { getOrCreateHeadlessPool, HEADLESS_POOL_ARGUMENT_SETS } from '@things-factory/shell'
import { pubsub } from '@things-factory/shell'
import { fonts } from './fonts.js'

// Custom setup function for label page
async function setupLabelPage(browser: any) {
  const protocol = 'http'
  const host = 'localhost'
  const path = '/internal-label-command-view'
  const port = process.env.PORT
  const url = `${protocol}://${host}:${port}${path}`

  const page = await browser.newPage()
  const [fontsToUse, fontStyles] = await fonts()

  await page.setRequestInterception(true)

  page.on('console', async msg => {
    const args = await Promise.all(msg.args().map(arg => arg.jsonValue().catch(() => undefined)))

    if (args.some(a => a !== undefined)) {
      console.log(`[headless ${msg.type()}]`, ...args.filter(a => a !== undefined))
    } else {
      const text = msg.text()
      if (text) console.log(`[headless ${msg.type()}]`, text)
    }
  })

  page.on('pageerror', error => {
    console.error(`[headless pageerror] ${error.message}`)
    console.error(error.stack)
  })

  page.on('error', error => {
    console.error(`[headless fault] ${error}`)
    console.error(error.stack)
  })

  page.on('requestfailed', request => {
    console.error('Request failed:', request.url())
  })

  page.on('request', request => {
    if (request.url() === url) {
      request.continue({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        postData: JSON.stringify({
          fonts: fontsToUse,
          fontStyles
        })
      })
    } else {
      request.continue()
    }
  })

  await page.goto(url, { timeout: 0, waitUntil: 'load' })

  return { browser, page }
}

/**
 * 라벨 출력은 (자동 출고 워크플로우 등) 즉시성 요구 — cold start 비용 (browser+page
 * setup ~2-3 초) 이 사용자 경험에 직결. min=1 로 최소 1 개 warm 유지가 기본.
 * 사용 빈도가 매우 낮은 사이트는 환경 변수로 0 까지 낮출 수 있다.
 *
 *   HEADLESS_POOL_LABEL_MIN  (default 1)
 *   HEADLESS_POOL_LABEL_MAX  (default 10)
 */
const LABEL_POOL_MIN = parseInt(process.env.HEADLESS_POOL_LABEL_MIN ?? '1', 10)
const LABEL_POOL_MAX = parseInt(process.env.HEADLESS_POOL_LABEL_MAX ?? '10', 10)

// Create the label pool instance with custom setup
const labelPool = getOrCreateHeadlessPool('label', {
  min: LABEL_POOL_MIN,
  max: LABEL_POOL_MAX,
  args: [
    ...HEADLESS_POOL_ARGUMENT_SETS.basic,
    ...HEADLESS_POOL_ARGUMENT_SETS.keychain_safe
  ],
  acquireTimeoutMillis: 15000,
  testOnBorrow: true,
  enableCleanup: true,
  customSetup: setupLabelPage
})

// Font change event subscription
setTimeout(async () => {
  const eventSource = pubsub.subscribe('notify-font-changed')
  for await (const data of eventSource) {
    // Reset the pool when fonts change
    await labelPool.reset()
  }
})

/**
 * Get the label headless pool
 * @returns Pool instance with acquire/release methods
 */
export function getHeadlessPool() {
  return {
    acquire: () => labelPool.acquire(),
    release: (resource: any) => labelPool.release(resource),
    size: 0, // These will be dynamically calculated if needed
    available: 0,
    borrowed: 0,
    pending: 0,
    max: LABEL_POOL_MAX,
    min: LABEL_POOL_MIN,
    clear: () => labelPool.reset() // For font change compatibility
  }
}